/**
 * User Service
 * 
 * Service for handling user-related API calls.
 */

import { apiConfig } from "@/config/api";
import { apiFetch, HEAVY_API_TIMEOUT_MS } from "@/lib/apiFetch";
import type { User } from "@/services/authService";
import { parseFlatMembers, type FlatMembersResponse } from "@/services/users/flat";

export interface MemberFilters {
  cursor?: string;
  limit?: number;
  direction?: "next" | "previous";
  search?: string; // Search term for filtering members
  province?: string; // Filter by province
  city?: string; // Filter by city (kabupaten)
  startDate?: string; // Filter by start date (ISO date string)
  endDate?: string; // Filter by end date (ISO date string)
  dateField?: "tanggal_terbit" | "masa_laku" | "masa_laku_kta"; // Field to filter by date range
  tipe_iar?: string; // Filter by IAR type
  sortBy?: "nama_lengkap" | "callsign" | "tanggal_terbit" | "masa_laku" | "provinsi" | "status_callsign" | "status_keanggotaan" | "_id"; // Field to sort by
  sortOrder?: "asc" | "desc"; // Sort order
  /** Filter by silent key flag (members API). */
  isSilentKey?: boolean;
  /** Request flattened (positional-array) response for smaller payload. */
  flat?: boolean;
}

export interface Member {
  masa_laku_kta: string | null;
  _id: string;
  id_client: string;
  nama_lengkap: string;
  no_hp: string;
  email: string;
  callsign: string;
  tanggal_terbit: string | null;
  masa_laku: string | null;
  provinsi: string;
  kabupaten: string;
  status_callsign: string;
  status_keanggotaan: number | null;
  tipe_iar?: string;
  isSilentKey?: boolean;
  silentKeyReportedBy?: string | null;
  silentKeyReportedAt?: string | null;
  silentKeyNotes?: string | null;
  lastLoginAt?: string | null;
  lastSeenAt?: string | null;
}

export interface MemberListResponse {
  data: Member[];
  nextCursor: string | null;
  previousCursor: string | null;
  hasNext: boolean;
  hasPrevious: boolean;
}

function parseFilenameFromContentDisposition(header: string | null): string | null {
  if (!header) return null;
  const m = header.match(/filename="([^"]*)"/);
  return m?.[1]?.length ? m[1] : null;
}

/** Matches server naming when Content-Disposition is not visible (e.g. CORS before expose fix). */
function clientMemberPdfFilename(
  userId: string,
  callsign: string | undefined,
  kind: "kta" | "tag" | "papanCallsign"
): string {
  const raw = (callsign || "").trim() || userId;
  const cs = raw.replace(/[/\\?%*:|"<>]/g, "_").trim() || "unknown";
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, "0");
  const timestamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}`;
  if (kind === "papanCallsign") {
    return `${cs} - PapanCallsign - ${timestamp}.pdf`;
  }
  if (kind === "tag") {
    return `${cs} - tag - ${timestamp}.pdf`;
  }
  return `${cs} - kta - ${timestamp}.pdf`;
}

class UserService {
  /**
   * Fetch members with pagination
   * @param filters - Filter options (cursor, limit, direction)
   * @returns Promise with paginated member data
   */
  async fetchMembers(filters: MemberFilters = {}): Promise<MemberListResponse> {
    try {
      const params = new URLSearchParams();

      if (filters.flat) {
        params.append("flat", "1");
      }
      
      if (filters.cursor) {
        params.append("cursor", filters.cursor);
      }
      
      if (filters.limit) {
        params.append("limit", filters.limit.toString());
      }
      
      if (filters.direction) {
        params.append("direction", filters.direction);
      }
      
      if (filters.search) {
        params.append("search", filters.search);
      }
      
      if (filters.province) {
        params.append("province", filters.province);
      }
      
      if (filters.city) {
        params.append("city", filters.city);
      }
      
      if (filters.startDate) {
        params.append("startDate", filters.startDate);
      }
      
      if (filters.endDate) {
        params.append("endDate", filters.endDate);
      }
      
      if (filters.dateField) {
        params.append("dateField", filters.dateField);
      }
      
      if (filters.tipe_iar) {
        params.append("tipe_iar", filters.tipe_iar);
      }
      
      if (filters.sortBy) {
        params.append("sortBy", filters.sortBy);
      }
      
      if (filters.sortOrder) {
        params.append("sortOrder", filters.sortOrder);
      }

      if (filters.isSilentKey === true) {
        params.append("isSilentKey", "true");
      } else if (filters.isSilentKey === false) {
        params.append("isSilentKey", "false");
      }

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/members?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (filters.flat) {
        return parseFlatMembers(data as FlatMembersResponse);
      }

      return data as MemberListResponse;
    } catch (error) {
      console.error("Error fetching members:", error);
      throw error;
    }
  }

  /**
   * List silent key users (requires `manage_silentkey`). Uses GET /api/users/silent-key.
   */
  async fetchSilentKeyUsers(filters: {
    cursor?: string;
    direction?: "next" | "previous";
    search?: string;
  } = {}): Promise<MemberListResponse> {
    const params = new URLSearchParams();
    if (filters.cursor) params.append("cursor", filters.cursor);
    if (filters.direction) params.append("direction", filters.direction);
    if (filters.search?.trim()) params.append("search", filters.search.trim().toUpperCase());

    const response = await apiFetch(
      `${apiConfig.baseURL}/api/users/silent-key?${params.toString()}`,
      {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        (errorData as { error?: string; message?: string }).error ||
          (errorData as { message?: string }).message ||
          `HTTP error! status: ${response.status}`
      );
    }

    return (await response.json()) as MemberListResponse;
  }

  /**
   * Set silent key flag (super admin only).
   */
  async updateUserSilentKey(
    userId: string,
    input:
      | boolean
      | {
          isSilentKey: boolean;
          silentKeyReportedBy?: string;
          silentKeyReportedAt?: string;
          silentKeyNotes?: string;
        }
  ): Promise<void> {
    const payload =
      typeof input === "boolean"
        ? { isSilentKey: input }
        : {
            isSilentKey: input.isSilentKey,
            silentKeyReportedBy: input.silentKeyReportedBy,
            silentKeyReportedAt: input.silentKeyReportedAt,
            silentKeyNotes: input.silentKeyNotes,
          };
    const response = await apiFetch(
      `${apiConfig.baseURL}/api/users/${encodeURIComponent(userId)}/silent-key`,
      {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const msg =
        (errorData as { error?: string; message?: string }).error ||
        (errorData as { message?: string }).message ||
        `HTTP error! status: ${response.status}`;
      throw new Error(msg);
    }
  }

  /**
   * Fetch user detail by ID
   * @param userId - User ID to fetch
   * @returns Promise with user data
   */
  async fetchUserDetailById(userId: string): Promise<User> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/${userId}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.user as User;
    } catch (error) {
      console.error("Error fetching user detail:", error);
      throw error;
    }
  }

  /**
   * Update a user's phone (no_hp). Caller must be that user or super admin (userLevel 3).
   */
  async updateUserPhone(userId: string, no_hp: string): Promise<void> {
    const response = await apiFetch(
      `${apiConfig.baseURL}/api/users/${userId}/phone`,
      {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ no_hp }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const msg =
        (errorData as { error?: string; message?: string }).error ||
        (errorData as { message?: string }).message ||
        `HTTP error! status: ${response.status}`;
      throw new Error(msg);
    }
  }

  /**
   * Fetch user by callsign
   * @param callsign - Callsign to fetch
   * @returns Promise with user data or null if not found
   */
  async fetchUserByCallsign(callsign: string): Promise<{ _id: string; callsign: string; nama_lengkap: string; name?: string } | null> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/callsign/${encodeURIComponent(callsign.toUpperCase())}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // 404 is expected for overseas/unregistered callsigns - return null gracefully
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.user;
    } catch (error) {
      // If it's a 404, return null (expected for overseas callsigns)
      if (error instanceof Error && (error.message.includes("404") || error.message.includes("not found"))) {
        return null;
      }
      console.error("Error fetching user by callsign:", error);
      throw error; // Re-throw other errors
    }
  }

  /**
   * Fetch user by client ID
   * @param id_client - Client ID to fetch
   * @returns Promise with member data or null if not found
   */
  async fetchUserByClientId(id_client: string): Promise<Member | null> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/client/${encodeURIComponent(id_client)}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.user as Member;
    } catch (error) {
      console.error("Error fetching user by client ID:", error);
      throw error;
    }
  }

  /**
   * Reset user password
   * @param userId - User ID to reset password for
   * @param password - New password to set
   * @returns Promise that resolves when password is reset
   */
  async resetPassword(userId: string, password: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/${userId}/reset-password`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ password }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return;
    } catch (error) {
      console.error("Error resetting password:", error);
      throw error;
    }
  }

  /**
   * Set user email (admin)
   */
  async setUserEmail(userId: string, email: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/${userId}/set-email`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error setting user email:", error);
      throw error;
    }
  }

  /**
   * Delete user (admin). confirmIdClient must match the user's id_client (or callsign if id_client empty).
   */
  async deleteUser(userId: string, confirmIdClient: string): Promise<void> {
    try {
      const response = await apiFetch(`${apiConfig.baseURL}/api/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ confirmIdClient }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  }

  /**
   * Sync user with SDPPI
   * @param userId - User ID to sync
   * @returns Promise that resolves when sync is complete
   */
  async syncUserWithSdppi(userId: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/${userId}/sync`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return;
    } catch (error) {
      console.error("Error syncing user with SDPPI:", error);
      throw error;
    }
  }

  /**
   * Get user statistics grouped by province
   * @returns Promise with array of province statistics
   */
  async getUserStatsByProvince(): Promise<{
    stats: Array<{ provinsi: string; kabupaten: string; count: number }>;
    locationScope?: {
      provinces: string[];
      cities: string[];
      level: "nasional" | "province" | "city" | null;
    };
  }> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/stats/province`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        stats: data.data,
        locationScope: data.locationScope,
      };
    } catch (error) {
      console.error("Error fetching province stats:", error);
      throw error;
    }
  }

  /**
   * Get dashboard statistics
   * @returns Promise with dashboard statistics
   */
  async getDashboardStats(): Promise<{
    stats: {
      totalUsers: number;
      activeUsers: number;
      totalBanks: number;
      activeBanks: number;
      totalBankAccounts: number;
      activeBankAccounts: number;
      totalPaymentRequests: number;
      pendingPaymentRequests: number;
      completedPaymentRequests: number;
    };
    locationScope?: {
      provinces: string[];
      cities: string[];
      level: "nasional" | "province" | "city" | null;
    };
  }> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/stats/dashboard`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        stats: data.data,
        locationScope: data.locationScope,
      };
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      throw error;
    }
  }

  /**
   * Sync user data from SDPPI to UserV2 by callsign (QSL sync)
   * @param callsign - Callsign to sync
   * @returns Promise with sync result
   */
  async syncQslByCallsign(callsign: string): Promise<{ 
    message: string; 
    user: { 
      id: string; 
      callsign: string; 
      nama_lengkap: string; 
      email: string; 
      id_client: string;
      jenis_kelamin?: string;
      kewarganegaraan?: string;
      nri?: string;
      no_hp?: string;
      jabatan_pekerjaan?: string;
      alamat?: string;
      provinsi?: string;
      kabupaten?: string;
      iar?: string;
      tanggal_terbit?: string | null;
      masa_laku?: string | null;
      tipe_iar?: string;
      jenis_permohonan?: string;
      status_callsign?: string;
      status_keanggotaan?: number;
      no_kta?: string;
      masa_laku_kta?: string | null;
      foto?: string;
      updatedAt?: string | null;
      createdAt?: string | null;
    } 
  }> {
    try {
      if (!callsign || typeof callsign !== 'string' || callsign.trim() === '') {
        throw new Error("Callsign is required");
      }

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/qsl/sync`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ callsign: callsign.trim() }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error syncing QSL by callsign:", error);
      throw error;
    }
  }

  /**
   * Download KTA PDF for a user
   * @param userId - User ID to generate PDF for
   * @param callsign - Optional; used for download filename if Content-Disposition is not readable (cross-origin)
   * @returns Promise that resolves when download starts
   */
  async downloadKTA(userId: string, callsign?: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/${userId}/pdf/kta`,
        {
          method: "GET",
          credentials: "include",
          timeoutMs: HEAVY_API_TIMEOUT_MS,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const fromHeader = parseFilenameFromContentDisposition(
        response.headers.get("Content-Disposition")
      );
      const filename = fromHeader ?? clientMemberPdfFilename(userId, callsign, "kta");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading KTA:", error);
      throw error;
    }
  }

  /**
   * Download Tag PDF for a user
   * @param userId - User ID to generate PDF for
   * @param callsign - Optional; used for download filename if Content-Disposition is not readable
   */
  async downloadTag(userId: string, callsign?: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/${userId}/pdf/tag`,
        {
          method: "GET",
          credentials: "include",
          timeoutMs: HEAVY_API_TIMEOUT_MS,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const fromHeader = parseFilenameFromContentDisposition(
        response.headers.get("Content-Disposition")
      );
      const filename = fromHeader ?? clientMemberPdfFilename(userId, callsign, "tag");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading Tag:", error);
      throw error;
    }
  }

  /**
   * Download Papan Callsign PDF for a user
   * @param userId - User ID to generate PDF for
   * @param callsign - Optional; used for download filename if Content-Disposition is not readable
   */
  async downloadCallsign(userId: string, callsign?: string): Promise<void> {
    try {
      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/${userId}/pdf/callsign`,
        {
          method: "GET",
          credentials: "include",
          timeoutMs: HEAVY_API_TIMEOUT_MS,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const fromHeader = parseFilenameFromContentDisposition(
        response.headers.get("Content-Disposition")
      );
      const filename = fromHeader ?? clientMemberPdfFilename(userId, callsign, "papanCallsign");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading Callsign:", error);
      throw error;
    }
  }

  /**
   * Export members to Excel based on filters
   * @param filters - Filter options (without pagination)
   * @returns Promise that resolves when download starts
   */
  async exportMembersToExcel(filters: Omit<MemberFilters, "cursor" | "limit" | "direction"> = {}): Promise<void> {
    try {
      const params = new URLSearchParams();
      
      if (filters.search) {
        params.append("search", filters.search);
      }
      
      if (filters.province) {
        params.append("province", filters.province);
      }
      
      if (filters.city) {
        params.append("city", filters.city);
      }
      
      if (filters.startDate) {
        params.append("startDate", filters.startDate);
      }
      
      if (filters.endDate) {
        params.append("endDate", filters.endDate);
      }
      
      if (filters.dateField) {
        params.append("dateField", filters.dateField);
      }
      
      if (filters.tipe_iar) {
        params.append("tipe_iar", filters.tipe_iar);
      }
      
      if (filters.sortBy) {
        params.append("sortBy", filters.sortBy);
      }
      
      if (filters.sortOrder) {
        params.append("sortOrder", filters.sortOrder);
      }

      const response = await apiFetch(
        `${apiConfig.baseURL}/api/users/members/export?${params.toString()}`,
        {
          method: "GET",
          credentials: "include",
          timeoutMs: HEAVY_API_TIMEOUT_MS,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get("Content-Disposition");
      let filename = "daftar-anggota.xlsx";
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Convert response to blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error exporting members to Excel:", error);
      throw error;
    }
  }
}

export const userService = new UserService();

